/**
 * Category x-axis that never drops a category: every tick prints, wrapped onto
 * up to three lines when its words fit the bar's slot, or turned 40° when a
 * single word is wider than the slot (phone widths, long site-type names).
 * The axis reserves exactly the height its labels need.
 */
import React from 'react';
import { CHART_Y_AXIS_WIDTH, CHART_MARGIN } from './FmChartChrome.constants';
import type { FmCategoryAxisLayout } from './categoryAxis.constants';
import {
  CATEGORY_CHAR_W,
  CATEGORY_LINE_H,
  CATEGORY_MAX_LINES,
  CATEGORY_ROTATE_DEG,
  CATEGORY_ROTATE_MAX_HEIGHT,
  CATEGORY_SLOT_PADDING,
  CATEGORY_TICK_GAP,
} from './categoryAxis.constants';
import { FmCategoryTick } from './FmCategoryTick';

export type { FmCategoryAxisLayout } from './categoryAxis.constants';

/** Greedy word wrap into lines of at most `maxChars`; null when a word cannot fit. */
function wrapWords(label: string, maxChars: number): string[] | null {
  const words = label.split(/\s+/u).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (word.length > maxChars) return null;
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length <= CATEGORY_MAX_LINES ? lines : null;
}

/**
 * Lay out the category labels for a plot `frameWidth` px wide. Before the
 * frame is measured the labels are laid out for one line each.
 */
export function categoryAxisLayout(
  labels: readonly string[],
  frameWidth: number | null,
): FmCategoryAxisLayout {
  const plotWidth =
    (frameWidth ?? Infinity) - CHART_Y_AXIS_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
  const slot = labels.length > 0 ? plotWidth / labels.length : plotWidth;
  const maxChars = Math.max(1, Math.floor((slot - CATEGORY_SLOT_PADDING) / CATEGORY_CHAR_W));
  const lines = new Map<string, readonly string[]>();
  for (const label of labels) {
    const wrapped = wrapWords(label, maxChars);
    if (wrapped === null) {
      const sin = Math.sin((CATEGORY_ROTATE_DEG * Math.PI) / 180);
      const maxRotatedChars = Math.floor(
        (CATEGORY_ROTATE_MAX_HEIGHT - CATEGORY_TICK_GAP - CATEGORY_LINE_H) /
          (CATEGORY_CHAR_W * sin),
      );
      const longest = Math.min(maxRotatedChars, Math.max(...labels.map((entry) => entry.length)));
      return {
        mode: 'rotate',
        height: Math.ceil(longest * CATEGORY_CHAR_W * sin + CATEGORY_TICK_GAP + CATEGORY_LINE_H),
        lines: new Map(),
        maxRotatedChars,
      };
    }
    lines.set(label, wrapped);
  }
  const lineCount = Math.max(1, ...[...lines.values()].map((entry) => entry.length));
  return {
    mode: 'wrap',
    height: CATEGORY_TICK_GAP + CATEGORY_LINE_H * lineCount + 4,
    lines,
  };
}

/** XAxis props for a category axis: every tick, the reserved height, the renderer. */
export function categoryAxisProps(
  layout: FmCategoryAxisLayout,
  format?: (value: string) => string,
) {
  return {
    interval: 0 as const,
    height: layout.height,
    tick: React.createElement(FmCategoryTick, { layout, format }),
  };
}
